import React, { useState, useEffect } from 'react';
import { Link as ScrollLink, Events, scrollSpy } from 'react-scroll';
import { Link as RouterLink, useLocation } from 'react-router-dom';

const Header = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuActive, setMenuActive] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true); // New state to track screen size
  const location = useLocation();

  useEffect(() => {
    // Function to check if the screen is desktop
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth > 991); // Desktop if width > 991px
    };

    // Initial check and event listener for resizing
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  useEffect(() => {
    // Initialize scrollspy
    Events.scrollEvent.register('begin', () => {});
    Events.scrollEvent.register('end', () => {});
    scrollSpy.update();

    // Scroll handler
    const handleScroll = () => {
      const sections = ['home', 'about', 'reviews', 'services', 'pricing', 'contact'];
      const scrollPosition = window.scrollY + window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Check if at top of page
      if (window.scrollY < 100) {
        setActiveSection('home');
      } else if (scrollPosition >= documentHeight - 100) {
        // Check if at bottom of page
        setActiveSection('contact');
      } else {
        // Find which section is currently in view
        const currentSection = sections.find(section => {
          const element = document.getElementById(section);
          if (element) {
            const rect = element.getBoundingClientRect();
            return rect.top <= 100 && rect.bottom >= 100;
          }
          return false;
        });

        if (currentSection) {
          setActiveSection(currentSection);
        }
      }

      // Set isScrolled based solely on whether we've scrolled away from the top
      setIsScrolled(window.scrollY > 30); // Using a smaller threshold of 30px
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      Events.scrollEvent.remove('begin');
      Events.scrollEvent.remove('end');
    };
  }, []);

  useEffect(() => {
    if (location.hash) {
      const section = location.hash.replace('#', '');
      const element = document.getElementById(section);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  const toggleMenu = () => {
    setMenuActive(!menuActive);
  };

  const closeMenu = () => {
    setMenuActive(false);
  };

  return (
    <header 
      className={`header-area header-sticky fixed w-full z-50 ${
        isScrolled && isDesktop ? 'background-header' : ''
      }`} 
    >
      <div className="container mx-auto flex justify-between items-center px-4 pb-2">
        <RouterLink to="/" className="logo">
          <img className="h-12 sm:h-16 md:h-16 md:mt-5 mt-4" src="/assets/images/JoltChat_Logo.png" alt="JoltChat Logo" />
        </RouterLink>
        
        {/* Mobile menu toggle button */}
        <button 
          className="menu-trigger md:hidden text-black focus:outline-none"
          onClick={toggleMenu}
          aria-label="Toggle menu"
        >
          <svg 
            className="w-8 h-8" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24" 
            xmlns="http://www.w3.org/2000/svg"
          >
            {menuActive ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
        
        {/* Desktop navigation */}
        <nav className="main-nav hidden md:block">
          <ul className="nav flex space-x-3">
            {['home', 'about', 'reviews', 'services'].map(section => (
              <li key={section} className="scroll-to-section">
                <RouterLink
                  to={`/#${section}`}
                  className={`cursor-pointer text-white hover:text-gray-400 ${
                    activeSection === section ? 'active border-b-2 border-white' : ''
                  }`}
                >
                {section === 'services' ? 'How It Works' : section.charAt(0).toUpperCase() + section.slice(1)}
                </RouterLink>
              </li>
            ))}
            <li className="scroll-to-section">
              <RouterLink
                to="/features"
                className={`cursor-pointer text-white hover:text-gray-400 ${
                  location.pathname === '/features' ? 'active border-b-2 border-white' : ''
                }`}
              >
                Features
              </RouterLink>
            </li>
            {['pricing', 'contact'].map(section => (
              <li key={section} className="scroll-to-section">
                <RouterLink
                  to={`/#${section}`}
                  className={`cursor-pointer text-white hover:text-gray-400 ${
                    activeSection === section ? 'active border-b-2 border-white' : ''
                  }`}
                >
                {section.charAt(0).toUpperCase() + section.slice(1)}
                </RouterLink>
              </li>
            ))}
            <li className="scroll-to-section" id="loginButton">
              <div className="border-first-button">
                <RouterLink to="/sign-in" className="text-white py-2 px-4 rounded-full">Login</RouterLink>
              </div>
            </li>
          </ul>
        </nav>
      </div>
      
      {/* Mobile navigation overlay */}
      {menuActive && (
        <div className="md:hidden bg-white py-4 px-4 absolute w-full shadow-lg rounded-lg mobile-menu">
          <ul className="flex flex-col space-y-4">
            {['home', 'about', 'reviews', 'services'].map(section => (
              <li key={section} className="scroll-to-section py-2 border-b border-gray-700">
                <RouterLink
                  to={`/#${section}`}
                  className={`cursor-pointer text-black hover:text-gray-400 block ${
                    activeSection === section ? 'active font-bold' : ''
                  }`}
                  onClick={closeMenu}
                >
                {section === 'services' ? 'How It Works' : section.charAt(0).toUpperCase() + section.slice(1)}
                </RouterLink>
              </li>
            ))}
            <li className="scroll-to-section py-2 border-b border-gray-700">
              <RouterLink
                to="/features"
                className={`cursor-pointer text-black hover:text-gray-400 block ${
                  location.pathname === '/features' ? 'active font-bold' : ''
                }`}
                onClick={closeMenu}
              >
                Features
              </RouterLink>
            </li>
            {['pricing', 'contact'].map(section => (
              <li key={section} className="scroll-to-section py-2 border-b border-gray-700">
                <RouterLink
                  to={`/#${section}`}
                  className={`cursor-pointer text-black hover:text-gray-400 block ${
                    activeSection === section ? 'active font-bold' : ''
                  }`}
                  onClick={closeMenu}
                >
                {section.charAt(0).toUpperCase() + section.slice(1)}
                </RouterLink>
              </li>
            ))}
            <li className="scroll-to-section py-2" id="loginButton">
              <div className="border-first-button mt-2">
                <RouterLink 
                  to="/sign-in" 
                  className="text-white py-2 px-4 rounded-full inline-block"
                  onClick={closeMenu}
                >
                  Login
                </RouterLink>
              </div>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
};

export default Header;